/**
 * Claim Detail Page - View and manage a single reimbursement claim
 */

import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { reimbursementApi } from '../api/reimbursementApi'
import type {
  ClaimStatus,
  CreateClaimItemInput,
  UpdateClaimInput,
  UpdateClaimItemInput,
  ClaimItem,
} from '../types/reimbursement.types'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

const getStatusColor = (status: ClaimStatus) => {
  switch (status) {
    case 'draft':
      return 'bg-gray-100 text-gray-800'
    case 'submitted':
      return 'bg-blue-100 text-blue-800'
    case 'approved':
      return 'bg-green-100 text-green-800'
    case 'rejected':
      return 'bg-red-100 text-red-800'
    case 'paid':
      return 'bg-emerald-100 text-emerald-800'
    case 'cancelled':
      return 'bg-gray-100 text-gray-600'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatCurrency = (amount: number, currency: string = 'IDR') => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const ClaimDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  useNavigate()
  const queryClient = useQueryClient()
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const { user } = useAuth()

  // Check if user is admin or in Finance division
  const isAdmin = user?.roles?.some((role) => role.name === 'admin')
  const isFinanceDivision = user?.division_name?.toLowerCase() === 'finance'
  const canMarkAsPaid = isAdmin || isFinanceDivision

  // UI State
  const [showAddItem, setShowAddItem] = useState(false)
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null)
  const [editingClaimInfo, setEditingClaimInfo] = useState(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve')
  const [approvalComments, setApprovalComments] = useState('')

  // Form State - New Item
  const [newItem, setNewItem] = useState({
    category_id: '',
    expense_date: '',
    description: '',
    merchant_name: '',
    amount: '',
  })

  // Form State - Edit Claim Info
  const [editClaimForm, setEditClaimForm] = useState({
    title: '',
    description: '',
    payment_method: '' as 'payroll' | 'bank_transfer' | 'cash',
    cost_center: '',
  })

  // Form State - Edit Item
  const [editItemForm, setEditItemForm] = useState({
    expense_date: '',
    description: '',
    merchant_name: '',
    amount: '',
  })

  // Fetch claim details
  const { data: claimData, isLoading, error } = useQuery({
    queryKey: ['reimbursementClaim', id],
    queryFn: () => reimbursementApi.getClaim(id!),
    enabled: !!id,
  })

  // Fetch categories for adding new items
  const { data: categoriesData } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: reimbursementApi.getCategories,
  })

  const categories = categoriesData?.data || []
  const claim = claimData?.data

  // Check if current user is the claim owner
  const isOwner = claim && user && claim.user_id === user.id

  // Check if current user can approve this claim
  const canApprove = (() => {
    if (!user || !claim) return false
    if (claim.status !== 'submitted') return false

    // Finance can approve any claim
    if (isFinanceDivision) return true

    // Direct manager can approve their team's claims
    if (claim.user?.manager_id === user.id) return true

    return false
  })()

  // Initialize edit form when claim data loads
  useEffect(() => {
    if (claim) {
      setEditClaimForm({
        title: claim.title || '',
        description: claim.description || '',
        payment_method: claim.payment_method || 'payroll',
        cost_center: claim.cost_center || '',
      })
    }
  }, [claim])

  // Mutations
  const submitMutation = useMutation({
    mutationFn: () => reimbursementApi.submitClaim(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaims'] })
      toast.success('Claim submitted for approval')
      setSubmitError(null)
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error?.message || 'Failed to submit claim'
      setSubmitError(errorMessage)
      toast.error(errorMessage)
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => reimbursementApi.cancelClaim(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaims'] })
      toast.success('Claim cancelled')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to cancel claim')
    },
  })

  const updateClaimMutation = useMutation({
    mutationFn: (data: UpdateClaimInput) => reimbursementApi.updateClaim(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaims'] })
      toast.success('Claim updated')
      setEditingClaimInfo(false)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to update claim')
    },
  })

  const addItemMutation = useMutation({
    mutationFn: (data: CreateClaimItemInput) => reimbursementApi.addClaimItem(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      toast.success('Item added')
      setShowAddItem(false)
      setNewItem({
        category_id: '',
        expense_date: '',
        description: '',
        merchant_name: '',
        amount: '',
      })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to add item')
    },
  })

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: UpdateClaimItemInput }) =>
      reimbursementApi.updateClaimItem(id!, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      toast.success('Item updated')
      setEditingItemId(null)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to update item')
    },
  })

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => reimbursementApi.deleteClaimItem(id!, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      toast.success('Item removed')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to remove item')
    },
  })

  const uploadReceiptMutation = useMutation({
    mutationFn: ({ itemId, file }: { itemId: string; file: File }) =>
      reimbursementApi.uploadReceipt(id!, itemId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      toast.success('Receipt uploaded')
      setUploadingItemId(null)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to upload receipt')
      setUploadingItemId(null)
    },
  })

  const deleteReceiptMutation = useMutation({
    mutationFn: ({ itemId, receiptId }: { itemId: string; receiptId: string }) =>
      reimbursementApi.deleteReceipt(id!, itemId, receiptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      toast.success('Receipt removed')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to remove receipt')
    },
  })

  const markPaidMutation = useMutation({
    mutationFn: () => reimbursementApi.markClaimPaid(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaims'] })
      queryClient.invalidateQueries({ queryKey: ['claimsReadyForPayment'] })
      toast.success('Claim marked as paid')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to mark claim as paid')
    },
  })

  const approveMutation = useMutation({
    mutationFn: (comments: string) => reimbursementApi.approveClaim(id!, { comments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaims'] })
      queryClient.invalidateQueries({ queryKey: ['pendingReimbursementApprovals'] })
      toast.success('Claim approved successfully')
      setShowApprovalModal(false)
      setApprovalComments('')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to approve claim')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (comments: string) => reimbursementApi.rejectClaim(id!, { comments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaim', id] })
      queryClient.invalidateQueries({ queryKey: ['reimbursementClaims'] })
      queryClient.invalidateQueries({ queryKey: ['pendingReimbursementApprovals'] })
      toast.success('Claim rejected')
      setShowApprovalModal(false)
      setApprovalComments('')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to reject claim')
    },
  })

  const handleApprovalSubmit = () => {
    if (approvalAction === 'reject' && !approvalComments.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }
    if (approvalAction === 'approve') {
      approveMutation.mutate(approvalComments)
    } else {
      rejectMutation.mutate(approvalComments)
    }
  }

  const handleFileSelect = (itemId: string, files: FileList | null) => {
    if (!files || files.length === 0) return
    const file = files[0]

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or PDF files.')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.')
      return
    }

    setUploadingItemId(itemId)
    uploadReceiptMutation.mutate({ itemId, file })
  }

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newItem.category_id || !newItem.expense_date || !newItem.description || !newItem.amount) {
      toast.error('Please fill all required fields')
      return
    }
    addItemMutation.mutate({
      category_id: newItem.category_id,
      expense_date: newItem.expense_date,
      description: newItem.description,
      merchant_name: newItem.merchant_name || undefined,
      amount: parseFloat(newItem.amount),
    })
  }

  const handleUpdateClaim = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editClaimForm.title) {
      toast.error('Title is required')
      return
    }
    updateClaimMutation.mutate({
      title: editClaimForm.title,
      description: editClaimForm.description || undefined,
      payment_method: editClaimForm.payment_method,
      cost_center: editClaimForm.cost_center || undefined,
    })
  }

  const startEditingItem = (item: ClaimItem) => {
    setEditingItemId(item.id)
    setEditItemForm({
      expense_date: item.expense_date.split('T')[0],
      description: item.description,
      merchant_name: item.merchant_name || '',
      amount: item.amount.toString(),
    })
  }

  const handleUpdateItem = (e: React.FormEvent, itemId: string) => {
    e.preventDefault()
    if (!editItemForm.description || !editItemForm.amount || !editItemForm.expense_date) {
      toast.error('Please fill all required fields')
      return
    }
    updateItemMutation.mutate({
      itemId,
      data: {
        expense_date: editItemForm.expense_date,
        description: editItemForm.description,
        merchant_name: editItemForm.merchant_name || undefined,
        amount: parseFloat(editItemForm.amount),
      },
    })
  }

  if (isLoading) return <LoadingPage />

  if (error || !claim) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-red-600">Failed to load claim</p>
          <Link to="/reimbursement" className="mt-4 text-red-600 hover:underline">
            Back to claims
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{claim.claim_number}</h1>
            <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(claim.status)}`}>
              {claim.status.replace('_', ' ')}
            </span>
          </div>
          <p className="text-gray-600">{claim.title}</p>
        </div>
        <Link
          to="/reimbursement"
          className="text-gray-600 hover:text-gray-900"
        >
          &larr; Back to claims
        </Link>
      </div>

      {/* Claim Info */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Claim Information</h2>
          {isOwner && claim.can_be_edited && !editingClaimInfo && (
            <button
              onClick={() => setEditingClaimInfo(true)}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Edit
            </button>
          )}
        </div>

        {editingClaimInfo ? (
          <form onSubmit={handleUpdateClaim} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Title *</label>
                <input
                  type="text"
                  value={editClaimForm.title}
                  onChange={(e) => setEditClaimForm({ ...editClaimForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Payment Method</label>
                <select
                  value={editClaimForm.payment_method}
                  onChange={(e) => setEditClaimForm({ ...editClaimForm, payment_method: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="payroll">Payroll</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Cost Center</label>
                <input
                  type="text"
                  value={editClaimForm.cost_center}
                  onChange={(e) => setEditClaimForm({ ...editClaimForm, cost_center: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Description</label>
                <textarea
                  value={editClaimForm.description}
                  onChange={(e) => setEditClaimForm({ ...editClaimForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={updateClaimMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {updateClaimMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingClaimInfo(false)
                  setEditClaimForm({
                    title: claim.title || '',
                    description: claim.description || '',
                    payment_method: claim.payment_method || 'payroll',
                    cost_center: claim.cost_center || '',
                  })
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Created By</p>
                <p className="font-medium">{claim.user?.full_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created Date</p>
                <p className="font-medium">{format(new Date(claim.created_at), 'MMM dd, yyyy')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Period</p>
                <p className="font-medium">
                  {format(new Date(claim.period_year, claim.period_month - 1), 'MMMM yyyy')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Method</p>
                <p className="font-medium capitalize">{claim.payment_method.replace('_', ' ')}</p>
              </div>
              {claim.submitted_at && (
                <div>
                  <p className="text-sm text-gray-500">Submitted</p>
                  <p className="font-medium">{format(new Date(claim.submitted_at), 'MMM dd, yyyy')}</p>
                </div>
              )}
              {claim.approved_at && (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Approved Date</p>
                    <p className="font-medium">{format(new Date(claim.approved_at), 'MMM dd, yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Approved By</p>
                    <p className="font-medium">
                      {claim.approvals?.find(a => a.status === 'approved')?.approver?.full_name || 'System'}
                    </p>
                  </div>
                </>
              )}
              {claim.paid_at && (
                <div>
                  <p className="text-sm text-gray-500">Paid Date</p>
                  <p className="font-medium">{format(new Date(claim.paid_at), 'MMM dd, yyyy')}</p>
                </div>
              )}
              {claim.cost_center && (
                <div>
                  <p className="text-sm text-gray-500">Cost Center</p>
                  <p className="font-medium">{claim.cost_center}</p>
                </div>
              )}
              {claim.description && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="font-medium">{claim.description}</p>
                </div>
              )}
            </div>

            {/* Rejection reason */}
            {claim.rejection_reason && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg">
                <p className="text-sm text-red-800">
                  <span className="font-medium">Rejection Reason:</span> {claim.rejection_reason}
                </p>
              </div>
            )}

            {/* Policy violations warning */}
            {claim.has_policy_violations && (
              <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-800">
                  <span className="font-medium">Warning:</span> This claim has policy violations. Please review the items below.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Expense Items */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Expense Items</h2>
          {isOwner && claim.can_be_edited && (
            <button
              onClick={() => setShowAddItem(!showAddItem)}
              className="px-3 py-1 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
            >
              {showAddItem ? 'Cancel' : '+ Add Item'}
            </button>
          )}
        </div>

        {/* Add Item Form */}
        {showAddItem && (
          <form onSubmit={handleAddItem} className="mb-4 p-4 border border-red-200 rounded-lg bg-red-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Category *</label>
                <select
                  value={newItem.category_id}
                  onChange={(e) => setNewItem({ ...newItem, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Date *</label>
                <input
                  type="date"
                  value={newItem.expense_date}
                  onChange={(e) => setNewItem({ ...newItem, expense_date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Merchant</label>
                <input
                  type="text"
                  value={newItem.merchant_name}
                  onChange={(e) => setNewItem({ ...newItem, merchant_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Amount (IDR) *</label>
                <input
                  type="number"
                  value={newItem.amount}
                  onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Description *</label>
                <input
                  type="text"
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={addItemMutation.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
            >
              {addItemMutation.isPending ? 'Adding...' : 'Add Item'}
            </button>
          </form>
        )}

        {/* Items List */}
        {claim.items && claim.items.length > 0 ? (
          <div className="space-y-3">
            {claim.items.map((item) => (
              <div
                key={item.id}
                className={`p-4 border rounded-lg ${
                  item.policy_violation ? 'border-orange-300 bg-orange-50' : 'border-gray-200'
                }`}
              >
                {editingItemId === item.id ? (
                  /* Edit Item Form */
                  <form onSubmit={(e) => handleUpdateItem(e, item.id)} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Date *</label>
                        <input
                          type="date"
                          value={editItemForm.expense_date}
                          onChange={(e) => setEditItemForm({ ...editItemForm, expense_date: e.target.value })}
                          max={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Amount (IDR) *</label>
                        <input
                          type="number"
                          value={editItemForm.amount}
                          onChange={(e) => setEditItemForm({ ...editItemForm, amount: e.target.value })}
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Merchant</label>
                        <input
                          type="text"
                          value={editItemForm.merchant_name}
                          onChange={(e) => setEditItemForm({ ...editItemForm, merchant_name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Description *</label>
                        <input
                          type="text"
                          value={editItemForm.description}
                          onChange={(e) => setEditItemForm({ ...editItemForm, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={updateItemMutation.isPending}
                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
                      >
                        {updateItemMutation.isPending ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingItemId(null)}
                        className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  /* View Item */
                  <>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{item.description}</span>
                          {item.policy_violation && (
                            <span className="text-xs text-orange-600" title={item.violation_details}>
                              Policy Violation
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          <span>{item.category?.name}</span>
                          {item.merchant_name && <span> • {item.merchant_name}</span>}
                          <span> • {format(new Date(item.expense_date), 'MMM dd, yyyy')}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(item.amount, item.currency)}
                        </p>
                        {item.approved_amount !== undefined && item.approved_amount !== item.amount && (
                          <p className="text-sm text-green-600">
                            Approved: {formatCurrency(item.approved_amount, item.currency)}
                          </p>
                        )}
                        {isOwner && claim.can_be_edited && (
                          <div className="flex justify-end gap-2 mt-1">
                            <button
                              onClick={() => startEditingItem(item)}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                if (confirm('Are you sure you want to remove this item?')) {
                                  deleteItemMutation.mutate(item.id)
                                }
                              }}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Receipts Section */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-700">Receipts</span>
                        {item.receipts && item.receipts.length > 0 && (
                          <span className="text-xs text-gray-500">({item.receipts.length})</span>
                        )}
                      </div>

                      {/* Existing Receipts */}
                      {item.receipts && item.receipts.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {item.receipts.map((receipt) => (
                            <div
                              key={receipt.id}
                              className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded-lg text-sm"
                            >
                              {receipt.mime_type.startsWith('image/') ? (
                                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              )}
                              <span className="text-gray-700 max-w-[150px] truncate" title={receipt.file_name}>
                                {receipt.file_name}
                              </span>
                              <span className="text-gray-400">({formatFileSize(receipt.file_size)})</span>
                              <button
                                onClick={() => reimbursementApi.downloadReceipt(receipt.id, receipt.file_name)}
                                className="text-red-600 hover:text-red-800"
                                title="Download"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                              {isOwner && claim.can_be_edited && (
                                <button
                                  onClick={() => {
                                    if (confirm('Remove this receipt?')) {
                                      deleteReceiptMutation.mutate({ itemId: item.id, receiptId: receipt.id })
                                    }
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                  title="Remove"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Upload Receipt Button */}
                      {isOwner && claim.can_be_edited && (
                        <div>
                          <input
                            type="file"
                            ref={(el) => { fileInputRefs.current[item.id] = el }}
                            onChange={(e) => handleFileSelect(item.id, e.target.files)}
                            accept="image/jpeg,image/png,image/gif,application/pdf"
                            className="hidden"
                          />
                          <button
                            onClick={() => fileInputRefs.current[item.id]?.click()}
                            disabled={uploadingItemId === item.id}
                            className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            {uploadingItemId === item.id ? (
                              <>
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Uploading...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                </svg>
                                Upload Receipt
                              </>
                            )}
                          </button>
                          <p className="text-xs text-gray-400 mt-1">JPEG, PNG, GIF, or PDF (max 10MB)</p>
                        </div>
                      )}

                      {/* No receipts message for non-editable claims */}
                      {!claim.can_be_edited && (!item.receipts || item.receipts.length === 0) && (
                        <p className="text-sm text-gray-400">No receipts attached</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No expense items</p>
        )}

        {/* Total */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-900">Total</span>
            <div className="text-right">
              <p className="text-xl font-bold text-red-600">
                {formatCurrency(claim.total_amount, claim.currency)}
              </p>
              {claim.original_amount !== claim.total_amount && (
                <p className="text-sm text-gray-500 line-through">
                  {formatCurrency(claim.original_amount, claim.currency)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approval History */}
      {claim.approvals && claim.approvals.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Approval History</h2>
          <div className="space-y-3">
            {claim.approvals.map((approval) => (
              <div key={approval.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{approval.approver?.full_name}</span>
                    <span className="text-xs text-gray-500">({approval.approval_role})</span>
                  </div>
                  {approval.comments && (
                    <p className="text-sm text-gray-600 mt-1">{approval.comments}</p>
                  )}
                  {approval.action_date && (
                    <p className="text-xs text-gray-400 mt-1">
                      {format(new Date(approval.action_date), 'MMM dd, yyyy HH:mm')}
                    </p>
                  )}
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    approval.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : approval.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {approval.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submission Error */}
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800">Submission Failed</h3>
              <p className="text-sm text-red-700 mt-1">{submitError}</p>
              {/* Helpful suggestions based on error */}
              <div className="mt-2 text-sm text-red-600">
                {submitError.toLowerCase().includes('item') && (
                  <p>Please add at least one expense item to your claim before submitting.</p>
                )}
                {submitError.toLowerCase().includes('status') && (
                  <p>This claim has already been submitted or is in a status that cannot be submitted.</p>
                )}
                {submitError.toLowerCase().includes('permission') || submitError.toLowerCase().includes('authorization') || submitError.toLowerCase().includes('own') && (
                  <p>You can only submit claims that you created.</p>
                )}
              </div>
            </div>
            <button
              onClick={() => setSubmitError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Submission Requirements Warning */}
      {isOwner && claim.can_be_submitted && (!claim.items || claim.items.length === 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-amber-800">Cannot Submit Yet</h3>
              <p className="text-sm text-amber-700 mt-1">
                You need to add at least one expense item before submitting this claim.
              </p>
              <button
                onClick={() => setShowAddItem(true)}
                className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-amber-800 hover:text-amber-900"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Expense Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        {isOwner && claim.can_be_cancelled && (
          <button
            onClick={() => {
              if (confirm('Are you sure you want to cancel this claim?')) {
                cancelMutation.mutate()
              }
            }}
            disabled={cancelMutation.isPending}
            className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
          >
            {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Claim'}
          </button>
        )}
        {isOwner && claim.can_be_submitted && (
          <button
            onClick={() => {
              // Validate before confirming
              if (!claim.items || claim.items.length === 0) {
                toast.error('Please add at least one expense item before submitting')
                return
              }
              if (confirm('Are you sure you want to submit this claim for approval?')) {
                submitMutation.mutate()
              }
            }}
            disabled={submitMutation.isPending || !claim.items || claim.items.length === 0}
            className={`px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              claim.items && claim.items.length > 0
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500'
            }`}
          >
            {submitMutation.isPending ? 'Submitting...' : 'Submit for Approval'}
          </button>
        )}
        {canApprove && (
          <>
            <button
              onClick={() => {
                setApprovalAction('reject')
                setApprovalComments('')
                setShowApprovalModal(true)
              }}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
            >
              Reject
            </button>
            <button
              onClick={() => {
                setApprovalAction('approve')
                setApprovalComments('')
                setShowApprovalModal(true)
              }}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Approve
            </button>
          </>
        )}
        {canMarkAsPaid && claim.status === 'approved' && (
          <button
            onClick={() => {
              if (confirm('Confirm that payment has been processed for this claim?')) {
                markPaidMutation.mutate()
              }
            }}
            disabled={markPaidMutation.isPending}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {markPaidMutation.isPending ? 'Processing...' : 'Mark as Paid'}
          </button>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {approvalAction === 'approve' ? 'Approve Claim' : 'Reject Claim'}
            </h3>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">{claim.claim_number}</span> - {claim.title}
              </p>
              <p className="text-sm text-gray-500">
                {claim.user?.full_name} • {formatCurrency(claim.total_amount, claim.currency)}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {approvalAction === 'approve' ? 'Comments (optional)' : 'Rejection Reason *'}
              </label>
              <textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                rows={3}
                placeholder={approvalAction === 'approve' ? 'Add any comments...' : 'Please provide a reason for rejection'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                required={approvalAction === 'reject'}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowApprovalModal(false)
                  setApprovalComments('')
                }}
                disabled={approveMutation.isPending || rejectMutation.isPending}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprovalSubmit}
                disabled={approveMutation.isPending || rejectMutation.isPending}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  approvalAction === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {approveMutation.isPending || rejectMutation.isPending
                  ? 'Processing...'
                  : approvalAction === 'approve'
                  ? 'Approve Claim'
                  : 'Reject Claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClaimDetailPage
